import { useEffect, useRef, useState } from "react";
import {
  IconArrowRight,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { supabase } from "../../supabaseClient";
import styles from "./GlobalShop.module.css";

type ShopProduct = {
  id: string;
  name: string;
  image: string;
  shopUrl: string;
  priceMin: number | null;
  compareAtMin: number | null;
  badge: string | null;
  category?: string;
  tags?: string[];
};

type GlobalShopProps = {
  onShopAll: () => void;
};

// Display order: bracelets first (all kinds), then anklets, mani, vaastu,
// rudraksha, then anything else. Matched against category/name/tags.
const CAT_ORDER: ((h: string) => boolean)[] = [
  (h) => h.includes("bracelet"),
  (h) => h.includes("anklet"),
  (h) => h.includes("mani"),
  (h) => h.includes("vastu") || h.includes("vaastu"),
  (h) => h.includes("rudraksh"),
];

const rank = (p: ShopProduct) => {
  const h = `${p.category ?? ""} ${p.name} ${(p.tags ?? []).join(" ")}`.toLowerCase();
  const i = CAT_ORDER.findIndex((m) => m(h));
  return i === -1 ? CAT_ORDER.length : i;
};

// products never shown in the rail
const EXCLUDE = ["green aventurine"];
// pinned to the front ("hero") of the rail, in this order, bracelet preferred
const FEATURED = ["money magnet", "dhan yog", "amethyst"];

// round-robin across category buckets so the rail looks mixed, not grouped
// (bracelet, anklet, mani, vaastu, rudraksha, other, bracelet, ...)
const interleave = (items: ShopProduct[]) => {
  const buckets: ShopProduct[][] = [];
  for (const it of items) (buckets[rank(it)] ??= []).push(it);
  const out: ShopProduct[] = [];
  let added = true;
  while (added) {
    added = false;
    for (const b of buckets) {
      const next = b?.shift();
      if (next) {
        out.push(next);
        added = true;
      }
    }
  }
  return out;
};

const inr = (n: number) => "₹" + Math.round(n).toLocaleString("en-IN");

const discount = (p: ShopProduct) => {
  if (!p.compareAtMin || !p.priceMin || p.compareAtMin <= p.priceMin) return null;
  return Math.round(((p.compareAtMin - p.priceMin) / p.compareAtMin) * 100);
};

export default function GlobalShop({ onShopAll }: GlobalShopProps) {
  const [products, setProducts] = useState<ShopProduct[] | null>(null);
  const railRef = useRef<HTMLDivElement>(null);

  const scrollRail = (dir: number) =>
    railRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("shop-catalog");
        if (active && data?.products) {
          const pool = (data.products as ShopProduct[]).filter(
            (p) => !EXCLUDE.some((x) => (p.name ?? "").toLowerCase().includes(x))
          );
          const featured: ShopProduct[] = [];
          for (const key of FEATURED) {
            const lc = (p: ShopProduct) => (p.name ?? "").toLowerCase();
            const m =
              pool.find(
                (p) =>
                  !featured.includes(p) &&
                  lc(p).includes(key) &&
                  lc(p).includes("bracelet")
              ) ?? pool.find((p) => !featured.includes(p) && lc(p).includes(key));
            if (m) featured.push(m);
          }
          const rest = interleave(pool.filter((p) => !featured.includes(p)));
          setProducts([...featured, ...rest].slice(0, 12));
        }
      } catch {
        if (active) setProducts([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section className={styles.section} aria-labelledby="global-shop-title">
      <div className={styles.head}>
        <h2 id="global-shop-title" className={styles.title}>
          Remedies Matched to Your Stars
        </h2>
        <p className={styles.lead}>
          Energised bracelets, crystals, and yantras, hand-picked for your chart.
        </p>
      </div>

      <div className={styles.railWrap}>
        <button
          type="button"
          className={`${styles.nav} ${styles.navPrev}`}
          onClick={() => scrollRail(-1)}
          aria-label="Scroll left"
        >
          <IconChevronLeft size={20} />
        </button>
        <button
          type="button"
          className={`${styles.nav} ${styles.navNext}`}
          onClick={() => scrollRail(1)}
          aria-label="Scroll right"
        >
          <IconChevronRight size={20} />
        </button>
      <div className={styles.rail} ref={railRef}>
        {products === null
          ? Array.from({ length: 6 }).map((_, i) => (
              <div className={styles.skeleton} key={i} />
            ))
          : products.map((p) => {
              const off = discount(p);
              return (
                <a
                  className={styles.card}
                  key={p.id}
                  href={p.shopUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={styles.media}>
                    {p.image && <img src={p.image} alt={p.name} loading="lazy" />}
                    {p.badge && <span className={styles.badge}>{p.badge}</span>}
                    {off && <span className={styles.off}>{off}% OFF</span>}
                  </div>
                  <div className={styles.body}>
                    <h3 className={styles.name}>{p.name}</h3>
                    <div className={styles.priceRow}>
                      {p.priceMin != null && (
                        <span className={styles.price}>{inr(p.priceMin)}</span>
                      )}
                      {p.compareAtMin != null &&
                        p.priceMin != null &&
                        p.compareAtMin > p.priceMin && (
                          <span className={styles.compare}>{inr(p.compareAtMin)}</span>
                        )}
                    </div>
                  </div>
                </a>
              );
            })}

        {products !== null && (
          <button type="button" className={styles.viewAll} onClick={onShopAll}>
            <IconArrowRight size={26} />
            <span>View all remedies</span>
          </button>
        )}
      </div>
      </div>
    </section>
  );
}
